import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { BuildingLibraryIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useTeamSwitcher } from '../../hooks/useTeamId';

/**
 * Sidebar-mounted team switcher. Shows the active team and lets a user
 * who belongs to multiple tenants pick which one their API calls
 * operate against. Persists via activeTeamStore.
 *
 * Rendering rules:
 *   - Hidden entirely if the user belongs to exactly one team (no
 *     switcher is noise when there's nothing to switch to).
 *   - Shows a small "Owner" vs "Member" badge per team in the dropdown
 *     so consultants invited into a client tenant can see at a glance
 *     whether they're about to act as owner or as a guest.
 */
export default function TeamSwitcher() {
  const queryClient = useQueryClient();
  const { teams, activeTeamId, setActiveTeamId, ownedIds, isLoading } = useTeamSwitcher();

  if (isLoading) return null;
  if (teams.length <= 1) return null;

  const active = teams.find((t) => t.id === activeTeamId) ?? teams[0];
  const activeIsOwned = ownedIds.has(active.id);

  const handleSelect = (teamId: string) => {
    if (teamId === activeTeamId) return;
    setActiveTeamId(teamId);
    // Evict every team-scoped query so the new tenant's data loads cleanly.
    // removeQueries (not invalidateQueries) deletes the cached data outright —
    // invalidate only marks it stale and keeps serving the previous tenant's
    // results during the refetch, which flashes the wrong bank's data. Keep the
    // user's own team list (['teams', ...]) since it isn't tenant-scoped, so the
    // switcher itself doesn't blink.
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'teams' });
  };

  return (
    <div className="px-3 pt-3 pb-1 border-b border-rule">
      <Menu as="div" className="relative w-full">
        <Menu.Button className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-panel-2 border border-rule">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-teal-50 text-teal-700">
            <BuildingLibraryIcon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-ink truncate">{active.name}</div>
            <div className="text-xs text-ink-3">
              {activeIsOwned ? 'Owner' : 'Member'}
            </div>
          </div>
          <ChevronUpDownIcon className="h-4 w-4 text-ink-4 shrink-0" />
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-150"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <Menu.Items className="absolute left-0 right-0 z-50 mt-1 max-h-80 overflow-y-auto rounded-lg border border-rule bg-panel shadow-lg focus:outline-none">
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-ink-4">
              Switch Team
            </div>
            {teams.map((team) => {
              const isOwned = ownedIds.has(team.id);
              const isActive = team.id === active.id;
              return (
                <Menu.Item key={team.id}>
                  {({ active: hover }) => (
                    <button
                      type="button"
                      onClick={() => handleSelect(team.id)}
                      className={clsx(
                        'w-full flex items-center gap-3 px-3 py-2.5 text-left',
                        hover ? 'bg-panel-2' : 'bg-panel',
                      )}
                    >
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-panel-2 text-ink-3">
                        <BuildingLibraryIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-ink truncate">
                          {team.name}
                        </div>
                        <div className="text-xs text-ink-3">
                          {isOwned ? 'Owner' : 'Member'}
                        </div>
                      </div>
                      {isActive && <CheckIcon className="h-4 w-4 text-teal-600 shrink-0" />}
                    </button>
                  )}
                </Menu.Item>
              );
            })}
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
}
